/**
 * Lazy-loaded syntax highlighting using highlight.js
 *
 * Only loads highlight.js when the first code block is encountered.
 * Languages are bundled but loaded from a single chunk.
 */

// Supported languages for tech docs / API support
const SUPPORTED_LANGUAGES = [
  'javascript', 'typescript', 'python', 'bash', 'shell', 'json', 'php',
  'ruby', 'go', 'java', 'csharp', 'yaml', 'xml', 'sql',
  'swift', 'kotlin', 'rust', 'graphql', 'dockerfile',
] as const;

// Language aliases
const LANGUAGE_ALIASES: Record<string, string> = {
  js: 'javascript',
  ts: 'typescript',
  py: 'python',
  rb: 'ruby',
  sh: 'bash',
  yml: 'yaml',
};

type HLJSApi = {
  highlight: (code: string, options: { language: string }) => { value: string };
  getLanguage: (name: string) => unknown;
};

let hljsInstance: HLJSApi | null = null;
let loadingPromise: Promise<HLJSApi> | null = null;

/**
 * Lazy load highlight.js with common languages.
 */
async function loadHighlightJS(): Promise<HLJSApi> {
  if (hljsInstance) return hljsInstance;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    // Dynamic import - code-split by Vite
    const hljs = await import('highlight.js/lib/core');

    // Register languages
    const [
      javascript, typescript, python, bash, shell, json, php,
      ruby, go, java, csharp, yaml, xml, sql,
      swift, kotlin, rust, graphql, dockerfile,
    ] = await Promise.all([
      import('highlight.js/lib/languages/javascript'),
      import('highlight.js/lib/languages/typescript'),
      import('highlight.js/lib/languages/python'),
      import('highlight.js/lib/languages/bash'),
      import('highlight.js/lib/languages/shell'),
      import('highlight.js/lib/languages/json'),
      import('highlight.js/lib/languages/php'),
      import('highlight.js/lib/languages/ruby'),
      import('highlight.js/lib/languages/go'),
      import('highlight.js/lib/languages/java'),
      import('highlight.js/lib/languages/csharp'),
      import('highlight.js/lib/languages/yaml'),
      import('highlight.js/lib/languages/xml'),
      import('highlight.js/lib/languages/sql'),
      import('highlight.js/lib/languages/swift'),
      import('highlight.js/lib/languages/kotlin'),
      import('highlight.js/lib/languages/rust'),
      import('highlight.js/lib/languages/graphql'),
      import('highlight.js/lib/languages/dockerfile'),
    ]);

    hljs.default.registerLanguage('javascript', javascript.default);
    hljs.default.registerLanguage('typescript', typescript.default);
    hljs.default.registerLanguage('python', python.default);
    hljs.default.registerLanguage('bash', bash.default);
    hljs.default.registerLanguage('shell', shell.default);
    hljs.default.registerLanguage('json', json.default);
    hljs.default.registerLanguage('php', php.default);
    hljs.default.registerLanguage('ruby', ruby.default);
    hljs.default.registerLanguage('go', go.default);
    hljs.default.registerLanguage('java', java.default);
    hljs.default.registerLanguage('csharp', csharp.default);
    hljs.default.registerLanguage('yaml', yaml.default);
    hljs.default.registerLanguage('xml', xml.default);
    hljs.default.registerLanguage('sql', sql.default);
    hljs.default.registerLanguage('swift', swift.default);
    hljs.default.registerLanguage('kotlin', kotlin.default);
    hljs.default.registerLanguage('rust', rust.default);
    hljs.default.registerLanguage('graphql', graphql.default);
    hljs.default.registerLanguage('dockerfile', dockerfile.default);

    // Register aliases
    hljs.default.registerLanguage('js', javascript.default);
    hljs.default.registerLanguage('ts', typescript.default);
    hljs.default.registerLanguage('py', python.default);
    hljs.default.registerLanguage('rb', ruby.default);
    hljs.default.registerLanguage('sh', bash.default);
    hljs.default.registerLanguage('yml', yaml.default);

    hljsInstance = hljs.default as unknown as HLJSApi;
    return hljsInstance;
  })();

  return loadingPromise;
}

/**
 * Normalize language name (resolve aliases).
 */
function normalizeLanguage(lang: string): string {
  const lower = lang.toLowerCase().trim();
  return LANGUAGE_ALIASES[lower] || lower;
}

/**
 * Check if a language is supported.
 */
export function isLanguageSupported(lang: string): boolean {
  const normalized = normalizeLanguage(lang);
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(normalized) ||
    Object.keys(LANGUAGE_ALIASES).includes(normalized);
}

/**
 * Highlight code with the specified language.
 * Returns the original code if language is not supported or highlighting fails.
 */
export async function highlightCode(code: string, language: string): Promise<string> {
  const lang = normalizeLanguage(language);

  // Skip highlighting for unsupported languages
  if (!isLanguageSupported(lang)) {
    return escapeHtml(code);
  }

  try {
    const hljs = await loadHighlightJS();

    // Verify language is registered
    if (!hljs.getLanguage(lang)) {
      return escapeHtml(code);
    }

    const result = hljs.highlight(code, { language: lang });
    return result.value;
  } catch (error) {
    console.warn('[LizaDocs] Code highlighting failed:', error);
    return escapeHtml(code);
  }
}

/**
 * Escape HTML special characters.
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Pre-load highlight.js in the background.
 * Call this when a code block is first detected to warm the cache.
 */
export function preloadHighlightJS(): void {
  loadHighlightJS().catch(() => {
    // Ignore preload errors - will retry on actual highlight
  });
}
